/**
 * The header bell — unread badge + recent-items dropdown.
 *
 * MOUNTING THIS (for whoever owns `components/layout/TopBar.tsx`):
 *
 *     import { NotificationBell } from "@/components/notifications/NotificationBell";
 *
 * and render `<NotificationBell />` inside the `div.header-actions` row,
 * between `{isAdmin && <AdminScopeToggle compact />}` and
 * `<ThemeToggle variant="chrome" />`. It needs no props and no context beyond
 * the QueryClient the app already provides, and it renders its own trigger
 * with the same chrome treatment `ThemeToggle` uses, so it drops in without
 * any layout change.
 *
 * WHAT IT COSTS WHILE IT SITS THERE
 * ---------------------------------
 * One `GET /notifications/unread-count` per minute, returning `{"unreadCount":
 * n}` and nothing else. The feed itself is fetched only when the dropdown is
 * opened (`useNotificationFeed(..., enabled: open)`), so a user who never
 * opens the bell never pays for rendering a list they did not look at.
 *
 * NO RADIX. The repo deliberately has no popover primitive, so this uses the
 * same hand-rolled pattern as the TopBar's own avatar menu: a ref, a
 * `mousedown` outside-click listener, and close-on-route-change.
 */
import { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck, Settings2 } from "lucide-react";
import Link from "@/compat/link";
import { usePathname } from "@/compat/navigation";

import { cn } from "@/lib/utils";
import { NotificationRow } from "./NotificationItem";
import { NotificationPrefsPanel } from "./NotificationPrefsPanel";
import {
  useMarkAllRead,
  useMarkRead,
  useNotificationFeed,
  usePushPrefs,
  useUnreadCount,
} from "./useNotifications";

/** The dropdown is a preview, not the feed. "See all" goes to the full page. */
const PREVIEW_LIMIT = 8;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  const { data: unread = 0 } = useUnreadCount();
  const { data, isLoading } = useNotificationFeed(PREVIEW_LIMIT, open);
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  // Only fetched once the dropdown opens — same "don't pay for what nobody
  // looked at" posture as the feed itself.
  const { data: prefs } = usePushPrefs(open);

  const mutedUntil =
    prefs?.muteUntil && new Date(prefs.muteUntil).getTime() > Date.now() ? prefs.muteUntil : null;

  useEffect(() => {
    setOpen(false);
    setShowPrefs(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const items = data?.items ?? [];
  // Past 99 the exact number stops being information and starts being noise.
  const badge = unread > 99 ? "99+" : String(unread);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-[var(--chrome-text-2)] transition-colors hover:bg-[var(--chrome-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cp-accent)]"
      >
        <Bell className="h-4 w-4" />
        {mutedUntil && !unread && (
          <span
            className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border border-[var(--cp-surface)] bg-[var(--cp-ink-3)]"
            aria-hidden="true"
          />
        )}
        {unread > 0 && (
          <span
            className={cn(
              "absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1",
              "bg-[var(--cp-accent)] text-[10px] font-bold leading-none text-[var(--cp-accent-ink)]",
            )}
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="nav-dropdown-menu absolute right-0 top-full z-50 mt-2 w-[22rem] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg border border-[var(--cp-line)] bg-[var(--cp-surface)] shadow-[var(--cp-shadow-lg)]"
        >
          <div className="flex items-center justify-between border-b border-[var(--cp-line-2)] px-3 py-2">
            <span className="cp-micro-label">Notifications</span>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => markAllRead.mutate()}
                  disabled={markAllRead.isPending}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold text-[var(--cp-ink-3)] transition-colors hover:bg-[var(--cp-surface-2)] hover:text-[var(--cp-ink)] disabled:opacity-50"
                >
                  <CheckCheck className="h-3 w-3" />
                  Mark all read
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowPrefs((v) => !v)}
                aria-expanded={showPrefs}
                aria-label="Notification settings"
                className={cn(
                  "inline-flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-[var(--cp-surface-2)]",
                  showPrefs ? "text-[var(--cp-accent-ink)]" : "text-[var(--cp-ink-3)]",
                )}
              >
                <Settings2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {showPrefs ? (
            <NotificationPrefsPanel />
          ) : (
            <>
              {mutedUntil && (
                <div className="border-b border-[var(--cp-line-2)] bg-[var(--cp-surface-2)] px-3 py-1.5 text-[11px] text-[var(--cp-ink-2)]">
                  Muted for now — resume from the gear icon above.
                </div>
              )}
              <div className="max-h-[24rem] overflow-y-auto">
                {isLoading ? (
                  <p className="px-3 py-6 text-center text-xs text-[var(--cp-ink-3)]">Loading…</p>
                ) : items.length === 0 ? (
                  <p className="px-3 py-6 text-center text-xs text-[var(--cp-ink-3)]">
                    Nothing new. You are all caught up.
                  </p>
                ) : (
                  <div className="divide-y divide-[var(--cp-line-2)]">
                    {items.map((item) => (
                      <NotificationRow
                        key={item.id}
                        item={item}
                        compact
                        onMarkRead={(id) => markRead.mutate([id])}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="border-t border-[var(--cp-line-2)]">
            <Link
              href="/notifications"
              className="block px-3 py-2 text-center text-[12px] font-semibold text-[var(--cp-accent-ink)] transition-colors hover:bg-[var(--cp-surface-2)]"
            >
              See all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
